import Button, { ButtonVariants } from "@aspen/MobiusOverrides/Button/Button";
import ProductImage from "@aspen/Overrides/Components/ProductImage";
import AspenProductPrice from "@aspen/Store/Aspen/Components/AspenProductPrice";
import useClickOutside from "@aspen/Store/Aspen/Hooks/useClickOutside";
import Cart from "@aspen/Store/Aspen/Icons/Cart";
import PopupClose from "@aspen/Store/Aspen/Icons/PopupClose";
import AspenSettings from "@aspen/Store/Aspen/Types/AspenSettings";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { removeCartLine } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, CartModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import ProductDescription from "@insite/content-library/Components/ProductDescription";
import CartLineQuantity from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries, { breakpointKeys } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    numberOfVisibleCartLines = "numberOfVisibleCartLines",
    checkoutLinkButtonVariant = "checkoutLinkButtonVariant",
    cartLinkButtonVariant = "cartLinkButtonVariant",
    className = "className"
}

const mapStateToProps = (state: ApplicationState) => {
    const { MiniCartEnabled } = (getSettingsCollection(state) as any).aspenSettings as AspenSettings;
    return {
        shouldLoadCart: (!getCurrentCartState(state).value && !getCurrentCartState(state).isLoading) || ((getCurrentCartState(state)?.value?.lineCount || 0) > 0 && getCurrentCartState(state).value?.cartLines?.length === 0),
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        currentCart: getCurrentCartState(state).value,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        checkoutShipping: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        session: getSession(state),
        miniCartEnabled: MiniCartEnabled
    }
};

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    miniCart?: InjectableCss;
    hiddenBelowLarge?: InjectableCss;
    hiddenAboveMedium?: InjectableCss;
}
const breakpointKeyValues = {
    [breakpointKeys[0]]: 0,
    [breakpointKeys[1]]: 1,
    [breakpointKeys[2]]: 2,
    [breakpointKeys[3]]: 3,
    [breakpointKeys[4]]: 4,
};
export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
    miniCart: {
        css: css`
            svg {
                height: 100%;
                max-width: 100%;
                vertical-align: top;
            }
            .removeline-icon {
                svg{
                    height: 70%;
                    width: 70%;
                }
            }
            .as-btn {
                color: #fff;
                display: inline-block;
                font-size: 16px;
                letter-spacing: .48px;
                line-height: 1.2;
                text-align: center;
                transition: background .3s ease,color .3s ease;
                vertical-align: top;
            }
            .as-btn-primary {
                background: #c8102e;
                border-radius: 10px;
                color: #fff;
                font-weight: 700;
                &:hover {
                    background: #253746;
                    color: #fff;
                }
            }
            .as-btn-outline {
                border: 1px solid #253746;
                border-radius: 10px;
                color: #253746;
                font-weight: 700;
                &:hover {
                    background-color: #253746;
                    color: #fff;
                }
            }
            .as-mini-cart-ctas{
                background-color: ${getColor("secondary.main")};
                color: ${getColor("secondary.contrast")};
                &:hover{
                    background-color: ${getColor("secondary.main")};
                    color: ${getColor("secondary.contrast")};
                }
            }
            &.as-card-holder {
                background: #fff;
                box-shadow: 0 3px 6px rgba(0,0,0,.25);
                margin: 20px 0 0;
                padding: 22px 22px 14px;
                position: absolute;
                right: 0px;
                top: 100%;
                width: 100vw;
                margin: 42px 0 0;
                top:0px;
                .as-ttl {
                    color: #013d54;
                    display: block;
                    font-family: "Inter Tight", Arial;
                    font-size: 20px;
                    font-weight: 700;
                    line-height: 1.2;
                    margin: 0 0 10px;
                    padding-right: 25px;
                }
                .as-close {
                    position: absolute;
                    right: 19px;
                    top: 17px;
                    &:hover {
                        svg {
                            line {
                                stroke: #c8102e;
                            }
                        }
                    }
                    svg {
                        line {
                            transition: stroke .35s cubic-bezier(.3,.86,.36,.95);
                        }
                    }
                }
                .as-product-holder {
                    display: flex;
                }
                .as-image {
                    border: 2px solid #e5e6e5;
                    height: 176px;
                    margin-right: 18px;
                    min-width: 176px;
                    width: 176px;
                    img {
                        height: 100%;
                        vertical-align: top;
                        width: 100%;
                    }
                }
                .as-text-area {
                    flex: 1 1 auto;
                    padding: 23px 0 0;
                    a {
                        &:hover {
                            color: #c8102e;
                        }
                    }
                }
                .as-subtitle {
                    color: #013d54;
                    display: block;
                    font-size: 14px;
                    font-weight: 350;
                    letter-spacing: .01em;
                    margin-top: -10px;
                }
                .as-product-ttl {
                    color: #013d54;
                    display: block;
                    font-size: 12px;
                    font-weight: 700;
                    letter-spacing: .15px;
                    line-height: 1.2;
                    margin: 0 0 29px;
                }
                .as-text {
                    display: block;
                    font-family: ITC Avant Garde Gothic CE,Helvetica Neue,Arial,Noto Sans,sans-serif;
                    font-size: 12px;
                    line-height: 1.2;
                    margin: 0 0 4px;
                }
                .as-wrap-btn {
                    padding-top: 22px;
                    .as-btn {
                        display: block;
                        flex-grow: 1;
                        margin: 0 0 11px;
                        text-align: center;
                    }
                }
                &.empty{
                    min-height:auto;
                    .as-wrap-btn{
                        margin: 0 -8px 0;
                        padding-top: 0px;
                    }
                }
            }
            @media (min-width: 768px) {
                &.as-card-holder {
                    display: flex;
                    flex-direction: column;
                    min-height: 235px;
                    padding: 10px 12px;
                    right: -20px;
                    width: 420px;
                    .as-ttl {
                        font-size: 23px;
                        margin: 0 0 17px;
                    }
                    .as-close {
                        right: 22px;
                        top: 19px;
                    }
                    .as-image {
                        height: 130px;
                        margin-right: 12px;
                        min-width: 130px;
                        width: 130px;
                    }
                    .as-text-area {
                        padding: 19px 0 0;
                    }
                    .as-product-ttl {
                        font-size: 15px;
                        margin: 0px;
                    }
                    .as-text {
                        font-size: 14px;
                        margin: 0 0 4px;
                    }
                    .as-wrap-btn {
                        display: flex;
                        justify-content: space-between;
                        margin: auto -8px 0;
                        padding-top: 10px;
                        .as-btn {
                            margin: 0 8px;
                        }
                    }
                    &.empty{
                        min-height:auto;
                    }
                }
            }
            @media (max-width: 1023px) {
                &.as-card-holder {
                    .as-subtitle {
                        display: none;
                    }
                }
            }
            @media (max-width: 767px) {
                &.as-card-holder {
                    .as-wrap-btn {
                        .as-btn {
                            border-radius: 20px;
                            border-width: 2px;
                            font-size: 20px;
                            letter-spacing: .6px;
                            padding: 17px 23px 16px;
                        }
                    }
                }
            }

            .as-cart-popup {
                .as-close {
                    position: absolute;
                    right: 0;
                    background: none;
                    top: 25px;
                    font-size: 10px;
                    svg {
                        line 
                        {
                            transition:stroke .35s cubic-bezier(.3,.86,.36,.95);
                        }
                    }
                    &:hover {
                        svg {
                            line 
                            {
                                stroke:#c8102e;
                            }
                        }
                    }
                }
            }

            @media (min-width: 768px)
            {
                .as-cart-popup {
                    .as-close {
                        right: -20px;
                        top: 25px;
                        font-size: 10px;
                    }
                }
                
            }
            a {
                background: transparent;
                color: #111;
                text-decoration: none;
                outline: 0 !important;
                &:active {
                    outline: 0;
                }
                &:hover {
                    outline: 0;
                    color: #000;
                    text-decoration: underline;
                    cursor: pointer;
                }
                &:focus {
                    color: #000;
                    text-decoration: underline;
                    outline: thin dotted;
                    outline: 5px auto -webkit-focus-ring-color;
                    outline-offset: -2px;
                }
            }
            .product-title {
                
                &:active {
                    outline: 0;
                    a {
                        outline: 0;
                    }
                }
                &:hover {
                    outline: 0!important;
                    color: #000!important;
                    -webkit-text-decoration: underline;
                    text-decoration: underline!important;
                    cursor: pointer;
                    a {
                        outline: 0!important;
                        color: #000!important;
                        -webkit-text-decoration: underline;
                        text-decoration: underline!important;
                        cursor: pointer;
                    }
                }
                &:focus {
                    color: #000!important;
                    text-decoration: underline!important;
                    outline: thin dotted!important;
                    outline: 5px auto -webkit-focus-ring-color!important;
                    outline-offset: -2px!important;
                    a {
                        color: #000!important;
                        text-decoration: underline!important;
                        outline: thin dotted!important;
                        outline: 5px auto -webkit-focus-ring-color!important;
                        outline-offset: -2px!important;
                    }
                }      
            }
            img {
                border: 0;
                vertical-align: middle;
                max-width: 100%;
                height: auto;
                -ms-interpolation-mode: bicubic;
                display: inline-block;
                vertical-align: middle;
            }
            table {
                border-collapse: collapse;
                border-spacing: 0;
                max-width: 100%;
                background-color: transparent;
            }
            td {
                padding: 0;
                padding-right: .25em;
            }
            th {
                padding: 0;
                text-align: left;
                padding: .4em;
            }
            * {
                -webkit-box-sizing: border-box;
                -moz-box-sizing: border-box;
                box-sizing: border-box;
                -moz-box-sizing: border-box;
                -webkit-box-sizing: border-box;
                box-sizing: border-box;
                &:before {
                    -webkit-box-sizing: border-box;
                    -moz-box-sizing: border-box;
                    box-sizing: border-box;
                    -moz-box-sizing: border-box;
                    -webkit-box-sizing: border-box;
                    box-sizing: border-box;
                }
                &:after {
                    -webkit-box-sizing: border-box;
                    -moz-box-sizing: border-box;
                    box-sizing: border-box;
                    -moz-box-sizing: border-box;
                    -webkit-box-sizing: border-box;
                    box-sizing: border-box;
                }
            }
            p {
                margin: 0 0 10px;
            }
            .container {
                margin-right: auto;
                margin-left: auto;
                padding-left: 15px;
                padding-right: 15px;
                &:before {
                    display: table;
                }
                &:after {
                    display: table;
                    clear: both;
                }
            }
            .row {
                margin-left: -15px;
                margin-right: -15px;
                &:before {
                    content: " ";
                    display: table;
                }
                &:after {
                    content: " ";
                    display: table;
                    clear: both;
                }
            }
            .col-md-12 {
                position: relative;
                min-height: 1px;
                padding-left: 15px;
                padding-right: 15px;
            }
            .col-xs-9 {
                position: relative;
                min-height: 1px;
                padding-left: 15px;
                padding-right: 15px;
                float: left;
                width: 75%;
            }
            .col-xs-3 {
                position: relative;
                min-height: 1px;
                padding-left: 15px;
                padding-right: 15px;
                float: left;
                width: 25%;
            }
            .table {
                width: 100%;
                margin-bottom: 20px;
                >tbody {
                    >tr {
                        >th {
                            padding: 8px;
                            line-height: 1.42857;
                            border-top: 1px solid #ddd;
                        }
                        >td {
                            padding: 8px;
                            line-height: 1.42857;
                            border-top: 1px solid #ddd;
                        }
                    }
                }
                th {
                    font-size: 14px;
                    background: #f1f1f1;
                    &:last-child{
                        width:20px;
                    }
                    &:first-child{
                        min-width:225px;
                    }
                }
            }
            .btn {
                display: inline-block;
                margin-bottom: 0;
                font-weight: normal;
                text-align: center;
                vertical-align: middle;
                cursor: pointer;
                background-image: none;
                border: 1px solid transparent;
                white-space: nowrap;
                padding: 6px 12px;
                font-size: 14px;
                line-height: 1.42857;
                border-radius: 2px;
                -webkit-user-select: none;
                -moz-user-select: none;
                -ms-user-select: none;
                -o-user-select: none;
                user-select: none;
                text-transform: uppercase;
                background: #00a7ce;
                border-color: #00a7ce;
                color: #fff;
                border-radius: 0 !important;
                font-weight: 700;
                font-size: 12px;
                &:focus {
                    outline: thin dotted;
                    outline: 5px auto -webkit-focus-ring-color;
                    outline-offset: -2px;
                    color: #333;
                    text-decoration: none;
                    background: #00a7ce;
                    border-color: #00a7ce;
                    color: #fff;
                    border-radius: 0 !important;
                    font-weight: 700;
                    font-size: 12px;
                }
                &:hover {
                    color: #333;
                    text-decoration: none;
                    background: #00a7ce;
                    border-color: #00a7ce;
                    color: #fff;
                    opacity: 0.87;
                    font-size: 12px;
                }
                &:active {
                    outline: 0;
                    background-image: none;
                    -webkit-box-shadow: inset 0 3px 5px rgba(0,0,0,0.125);
                    box-shadow: inset 0 3px 5px rgba(0,0,0,0.125);
                    background: #00a7ce;
                    border-color: #00a7ce;
                    color: #fff;
                    border-radius: 0 !important;
                    font-weight: 700;
                    font-size: 12px;
                }
            }
            .btn-primary {
                color: #fff;
                background-color: #333;
                border-color: #262626;
                &:hover {
                    color: #fff;
                    background-color: #1f1f1f;
                    border-color: #080808;
                }
                &:focus {
                    color: #fff;
                    background-color: #1f1f1f;
                    border-color: #080808;
                }
                &:active {
                    color: #fff;
                    background-color: #1f1f1f;
                    border-color: #080808;
                    background-image: none;
                }
            }
            .btn-group-lg {
                >.btn {
                    padding: 6px 14px;
                    font-size: 14px;
                    line-height: 1.33;
                    border-radius: 2px;
                }
            }
            .btn-group {
                position: relative;
                display: inline-block;
                vertical-align: middle;
                >.btn {
                    position: relative;
                    float: left;
                    &:hover {
                        z-index: 2;
                    }
                    &:focus {
                        z-index: 2;
                        outline: none;
                    }
                    &:active {
                        z-index: 2;
                    }
                    &:first-child {
                        margin-left: 0;
                        &:not(:last-child) {
                            &:not(.dropdown-toggle) {
                                border-bottom-right-radius: 0;
                                border-top-right-radius: 0;
                            }
                        }
                    }
                    &:last-child {
                        &:not(:first-child) {
                            border-bottom-left-radius: 0;
                            border-top-left-radius: 0;
                        }
                    }
                }
                .btn+.btn {
                    margin-left: -1px;
                }
            }
            .btn-group-justified {
                display: table;
                width: 100%;
                table-layout: fixed;
                border-collapse: separate;
                >.btn {
                    float: none;
                    display: table-cell;
                    width: 1%;
                }
            }
            .pull-right {
                float: right;
                float: right;
            }
            .text-bold {
                font-weight: bold;
            }
            .cart {
                &:hover {
                    .cart-content {
                        display: block;
                    }
                }
            }
            .cart-content {
                width: 100%;
                position: absolute;
                background-color: #fff;
                z-index: 9999;
                width: 100%;
                padding-left: 0;
                padding-right: 0;
                margin-left: 0;
                margin-right: 0;
                .row {
                    padding-left: 0;
                    padding-right: 0;
                    margin-left: 0;
                    margin-right: 0;
                }
            }
            .cart-table {
                font-size: 12px;
                min-width: 395px;
                th {
                    border: none;
                    text-transform: uppercase;
                    padding: 10px 17px;
                    &.center{
                        text-align: center;
                    }
                }
                td {
                    padding: 10px 17px;
                    &.center{
                        text-align: center;
                    }
                }
            }
            .cart-product {
                width: 60%;
            }
            .cart-product-img {
                float: left;
                margin-right: 10px;
            }
            .cart-product-nr {
                display: block;
                overflow: hidden;
            }
            .hover-product-image {
                max-width: 100%;
                display: block;
                margin: 0 0 20px;
                -webkit-box-shadow: 0 10px 10px -7px rgba(50,50,50,.5);
                -moz-box-shadow: 0 10px 10px -7px rgba(50,50,50,.5);
                box-shadow: 0 10px 10px -7px rgba(50,50,50,.5);
                border: 1px solid #ccc;
                width: 100%;
                box-shadow: none;
                border: none;
                margin-bottom: 0px;
            }
            .fa {
                display: inline-block;
                font: normal normal normal 14px/1 FontAwesome;
                font-size: inherit;
                text-rendering: auto;
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
            }
            .fa-remove {
                &:before {
                    content: "";
                }
            }
            .mini-cart-content {
                .row {
                    border-bottom: 1px solid #ccc;
                    &:last-child {
                        border-bottom: none;
                    }
                }
                .image-column {
                    padding-right: 4px;
                    padding-left: 4px;
                    padding-top: 4px;
                }
                .info-column {
                    padding-right: 4px;
                    padding-left: 4px;
                    padding-top: 4px;
                }
                p {
                    margin-bottom: 0;
                }
            }
            .js-removeFromBasket {
                position: absolute;
                right: 10px;
            }
                .cart {
                    .cart-content.container {
                        width: 100% !important;
                        width: 400px !important;
                        right: 0;
                        box-shadow: 4px 4px 4px 4px rgb(0 0 0 / 10%);
                    }
                }
                #minibasket {
                    .cart-content.container {
                        width: 100% !important;
                        width: 400px !important;
                        right: 0;
                        box-shadow: 4px 4px 4px 4px rgb(0 0 0 / 10%);
                    }
                }
                .cart-content {
                    .btn-group {
                        display: flex;
                        justify-content: space-between;
                        padding: 10px;
                        border-top: 1px solid #ddd;
                        >a {
                            width: 49%;
                        }
                    }
                    table {
                        th {
                            background: #f1f1f1;
                        }
                        margin-bottom: 0;
                        margin-left: -10px;
                    }
                    .js-removeFromBasket {
                        margin-top: -9px;
                    }
                .container {
                    >div {
                        padding: 0;
                    }
                }
            }
            .header {
                .cart-content {
                    .btn {
                        font-size: 12px;
                    }
                }
            }
            .sticky-header {
                img {
                    height: 44px;
                    transition: height 0.15s ease;
                }
            }
            @media (min-width: 768px) {
                .container {
                    width: 395px;
                }
            }
            @media (min-width: 992px) {
                
                .col-md-12 {
                    float: left;
                    width: 100%;
                }
            }
            @media (min-width: 1200px) {
                .hidden-lg {
                    display: none;
                }
            }
            @media (max-width: 767px) {
                .hidden-xs {
                    display: none;
                }
                .cart {
                    &:hover {
                        .cart-content {
                            display: block;
                            position: absolute;
                            background: #FFF;
                        }
                    }
                }
            }
            @media (min-width: 768px) and (max-width: 991px) {
                .hidden-sm {
                    display: none;
                }
            }
            @media (min-width: 992px) and (max-width: 1199px) {
                .hidden-md {
                    display: none;
                }
            }
            @media (max-width: 480px) {
                .cart-content {
                    .btn-group {
                        .btn {
                            font-size: 14px;
                        }
                    }
                }
            }
            @media (max-width:767px) {
                .hidden-xs {
                    display: none;
                }
            }
            .as-btn-outline {
                border: 1px solid #253746;
                border-radius: 10px;
                color: #253746!important;
                font-weight: 700;
                &:hover {
                    background-color: #253746!important;
                    color: #fff!important;
                }
            }

        `
    },
    hiddenBelowLarge: {
        css: css`
            ${({ theme }) => {
                const rules = Array(5);
                rules[breakpointKeyValues['lg'] - 1] = css`
                        display: none!important;
                    `;
                return breakpointMediaQueries(theme, rules, "max");
            }}
        `
    },
    hiddenAboveMedium: {
        css: css`
            ${({ theme }) => {
                const rules = Array(5);
                rules[breakpointKeyValues['md'] + 1] = css`
                        display: none!important;
                    `;
                return breakpointMediaQueries(theme, rules, "min");
            }}
        `
    }

};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
        [fields.numberOfVisibleCartLines]: number
        [fields.className]: string;
        [fields.cartLinkButtonVariant]: ButtonVariants;
        [fields.checkoutLinkButtonVariant]: ButtonVariants;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    checkoutShipping,
    session,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon, numberOfVisibleCartLines, cartLinkButtonVariant, checkoutLinkButtonVariant, className },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    currentCart,
    miniCartEnabled
}) => {
    const wrapperRef = useRef(null);
    useEffect(() => {
        loadCurrentCart({ shouldLoadFullCart: true, replaceCart: false });
    }, [shouldLoadCart]);

    useClickOutside(wrapperRef.current, () => {
        setCartDisplay(false);
    });
    const styles = useMergeStyles("cartLink", cartLinkStyles);
    const [cartDisplay, setCartDisplay] = useState(false);
    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink, miniCart } = styles;

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }
    const loadAndDisplayCart = () => {
        loadCurrentCart({
            shouldLoadFullCart: true, replaceCart: false, onSuccess(result) {
                setCartDisplay(true);
            },
        });
    }

    const onCartDisplayChange = () => {
        setCartDisplay(!cartDisplay);
    }
    const removeCartLineClickHandler = (showRemoveAction: boolean, cartLine: CartLineModel, cart: CartModel) => {
        if (!showRemoveAction) {
            return;
        }
        removeCartLine({ cartId: cart.id, cartLineId: cartLine.id }).finally(() => {
            loadAndDisplayCart();
        });

    };

    const cartLinkClickHandler = () => {
        window.location.href = cartUrl || "/Cart";
    };
    const checkoutLinkClickHandler = () => {
        window.location.href = checkoutShipping || "/Cart";
    };

    return (
        <>

            <Link {...cartLinkStyles.hiddenBelowLarge}
                as="a"
                data-test-selector="cartLink" className="as-opener" href={cartUrl} onMouseEnter={() => loadAndDisplayCart()} onMouseLeave={() => setCartDisplay(false)}
            >
                <Cart />
                <span style={{ display: 'table', visibility: 'hidden' }}>Cart</span>
            </Link>
            <Link  {...cartLinkStyles.hiddenAboveMedium}
                as="a"
                data-test-selector="cartLink" className="as-opener" href={cartUrl}
            >
                <Cart />
            </Link>
            <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            {miniCartEnabled && cartDisplay && <Typography as="div" {...miniCart} ref={wrapperRef} className={`as-card-holder  ${currentCart?.cartLines?.length ? 'has-content' : 'empty'}`} onMouseEnter={() => setCartDisplay(true)}
                onMouseLeave={() => setCartDisplay(false)} >
                {currentCart && <>
                    <div className="as-product-holder">
                        <div className="feature-basket">
                            <table className={`table cart-table hidden-xs hidden-sm  ${currentCart?.cartLines?.length ? 'has-content' : 'empty'}`}>
                                <thead>
                                    <tr>
                                        <th colSpan={2}>Product</th>
                                        <th className="center">Qty</th>
                                        {session.isAuthenticated && <th>Price</th>}
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentCart.cartLines?.slice(0, (numberOfVisibleCartLines || 5)).map(cartLine => {
                                        const showRemoveAction = !cartLine.isPromotionItem && currentCart.canModifyOrder;
                                        return (<>
                                            <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                                                <tr>
                                                    <td className="cart-product" style={{ width: '50px' }}>
                                                        <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                                                    </td>
                                                    <td className="cart-product product-title">
                                                        <Typography {...styles.productErpNumberText}>{cartLine.erpNumber}</Typography>
                                                        <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                                                    </td>
                                                    <td className="center">
                                                        <CartLineQuantity cart={currentCart} extendedStyles={styles.quantity} editable={false} label=" " />
                                                    </td>
                                                    {session.isAuthenticated && <td className="center">
                                                        <AspenProductPrice
                                                            product={cartLine}
                                                            currencySymbol={currentCart.currencySymbol}
                                                            showInvalidPriceMessage={false}
                                                            extendedStyles={styles.price}
                                                            showLabel={false}
                                                            showUnitOfMeasure={false}
                                                        />
                                                    </td>}
                                                    <td>
                                                        {showRemoveAction && (
                                                            <Clickable onClick={() => removeCartLineClickHandler(showRemoveAction, cartLine, currentCart)} data-test-selector="cartline_removeLine" className="removeline-icon">
                                                                <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                                                                <PopupClose />
                                                            </Clickable>
                                                        )}
                                                    </td>
                                                </tr>
                                            </CartLineContext.Provider>
                                        </>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {(!!currentCart.cartLines?.length) &&
                                <div className="row">
                                    <div className="col-md-12 pull-left">
                                        {currentCart.cartLines?.length > (numberOfVisibleCartLines || 5) && <Link
                                            as="a"
                                            href={cartUrl}
                                        >
                                            <span>{translate('Show All')}</span>
                                        </Link>}
                                    </div>
                                </div>}
                        </div>
                    </div>
                </>}

                <div className="as-wrap-btn">
                    <Button className={className} variant={cartLinkButtonVariant} onClick={cartLinkClickHandler}>
                        {translate("View Cart")}
                    </Button>
                    {session.isAuthenticated && currentCart && currentCart.lineCount > 0 && <Button className={className} variant={checkoutLinkButtonVariant} onClick={checkoutLinkClickHandler}>
                        {translate("Checkout")}
                    </Button>}
                </div>
            </Typography>}
        </>
    );
};
const cartLinkTab = {
    displayName: "Cart Link",
    sortOrder: 0,
};

const miniCartTab = {
    displayName: "Mini cart",
    sortOrder: 1,
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
                tab: cartLinkTab
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
                tab: cartLinkTab
            },
            {
                name: fields.numberOfVisibleCartLines,
                displayName: "Number Of Visible Cart Lines",
                editorTemplate: "IntegerField",
                min: 1,
                max: 10,
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 1,
                tab: miniCartTab
            },
            {
                name: fields.cartLinkButtonVariant,
                displayName: "Cart Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "Translatable",
            }, {
                name: fields.checkoutLinkButtonVariant,
                displayName: "Check out Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "Translatable",
            },
            {
                name: fields.className,
                displayName: "Button Class name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
